import React, { useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { Table } from 'antd';
import { Form } from 'react-final-form';
import axios from 'axios';
import CheckboxField from '@/shared/components/form/CheckboxField';
import { flashError, sendFlashMessage } from '@/shared/components/flash';
import { flattenHierarchy } from './flattenHierarchy';

// ICOV3-1572: fires per-field, immediately on that field's own change (see
// spec Step 13 reversal, 2026-07-13) — `fields` is always a single-key
// {[fieldName]: value} object. Both choices below save only that field,
// via the same cascade-hierarchy endpoint; "parent only" is just
// programIds: [programId], "entire hierarchy" adds selected descendants.
const HierarchySaveModal = ({
  isOpen,
  toggle,
  organizationId,
  programId,
  settingsGroup,
  fields,
  dispatch,
  onSuccess,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [hierarchyListData, setHierarchyListData] = useState([]);
  const [checkedState, setCheckedState] = useState([]);
  const [resultData, setResultData] = useState([]);

  const saveToProgramIds = (programIds) => axios.put(
    `/organization/${organizationId}/program/${programId}/cascade-hierarchy`,
    { settingsGroup, fields, programIds },
  );

  const handleParentOnly = () => {
    toggle();
    saveToProgramIds([programId])
      .then((res) => {
        if (res.status === 200) {
          dispatch(sendFlashMessage('Program has been updated', 'alert-success', 'top'));
          onSuccess(res.data);
        }
      })
      .catch((err) => {
        flashError(dispatch, err?.response?.data);
      });
  };

  const handleEntireHierarchy = async () => {
    try {
      const res = await axios.get(
        `/organization/${organizationId}/program/${programId}/hierarchy-prepare`,
      );
      if (res.status === 200) {
        const descendants = flattenHierarchy(res.data).filter(
          (item) => item.id !== programId,
        );
        setHierarchyListData(descendants);
        setCheckedState(descendants.map((item) => ({ checked: true, value: item.id })));
        toggle();
        setPickerOpen(true);
      }
    } catch (err) {
      flashError(dispatch, err?.response?.data);
      toggle();
    }
  };

  const togglePicker = () => setPickerOpen((prev) => !prev);
  const toggleResult = () => setResultOpen((prev) => !prev);

  const onSubmitPicker = () => {
    const selectedDescendantIds = checkedState
      .filter((item) => item.checked)
      .map((item) => item.value);
    const programIds = [programId, ...selectedDescendantIds];

    saveToProgramIds(programIds)
      .then((res) => {
        if (res.status === 200) {
          setResultData(res.data);
          setPickerOpen(false);
          setResultOpen(true);
          onSuccess(res.data);
        }
      })
      .catch((err) => {
        flashError(dispatch, err?.response?.data);
        setPickerOpen(false);
      });
  };

  const pickerColumns = [
    { title: 'Program ID', dataIndex: 'id', key: 'id', render: (text) => <a>{text}</a> },
    { title: 'Program Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Copy\\Update?',
      key: 'action',
      render: (_, record, index) => (
        <div className="checkbox-custom2">
          <CheckboxField
            name={`action-checkbox-${record.id}`}
            label="update"
            checked={checkedState[index].checked}
            onChange={() => {
              const updated = [...checkedState];
              updated[index].checked = !updated[index].checked;
              setCheckedState(updated);
            }}
          />
        </div>
      ),
    },
  ];

  const resultColumns = [
    { title: 'Program ID', dataIndex: 'id', key: 'id', render: (text) => <a>{text}</a> },
    { title: 'Program Name', dataIndex: 'name', key: 'name' },
    { title: 'Result', dataIndex: 'result', key: 'result' },
  ];

  return (
    <>
      <Modal className="modal-hierarchy-save modal-md" isOpen={isOpen} toggle={toggle}>
        <ModalHeader>
          <div>How would you like to save this change?</div>
        </ModalHeader>
        <ModalBody style={{ textAlign: 'left' }}>
          <Button outline className="btn btn-primary btn-sm" onClick={handleParentOnly}>
            No, only save to this program
          </Button>
          <Button
            outline
            className="btn btn-primary btn-sm"
            style={{ float: 'left' }}
            onClick={handleEntireHierarchy}
          >
            Yes, save to entire hierarchy
          </Button>
          <Button outline color="primary" className="btn btn-sm" style={{ float: 'right' }} onClick={toggle}>
            Cancel
          </Button>
        </ModalBody>
      </Modal>

      <Modal className="modal-hierarchy-save-list modal-lg" isOpen={pickerOpen} toggle={togglePicker}>
        <ModalHeader>
          <div>Please select the programs that you wish to update</div>
        </ModalHeader>
        <ModalBody className="modal-lg" style={{ textAlign: 'left' }}>
          <Form onSubmit={onSubmitPicker}>
            {({ handleSubmit }) => (
              <form className="form" onSubmit={handleSubmit}>
                <div style={{ padding: '10px' }}>
                  <Button outline className="btn btn-primary btn-sm" type="submit">
                    Save
                  </Button>
                  {hierarchyListData.length === 0 && (
                    <div>This program has no subprograms — only this program will be updated.</div>
                  )}
                </div>
                <Table
                  pagination={false}
                  rowKey="id"
                  columns={pickerColumns}
                  dataSource={hierarchyListData}
                />
              </form>
            )}
          </Form>
        </ModalBody>
      </Modal>

      <Modal className="modal-hierarchy-save-result modal-lg" isOpen={resultOpen} toggle={toggleResult}>
        <ModalHeader>
          <div>Update Results</div>
        </ModalHeader>
        <ModalBody className="modal-lg" style={{ textAlign: 'left' }}>
          <Table
            pagination={false}
            rowKey="id"
            columns={resultColumns}
            dataSource={resultData}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default HierarchySaveModal;
